import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import { AssistantRuntimeProvider } from '@assistant-ui/react';
import { useLangGraphRuntime, type LangChainMessage } from '@assistant-ui/react-langgraph';
import { useQueryClient } from '@tanstack/react-query';

import type { AgentSuggestion } from '../../api/agent';
import { useApiServices } from '../../api/services';
import { useAuth } from '../../hooks/useAuth';
import { useAgentThread } from '../../hooks/useAgentSidebar';
import { useScreenContext } from '../../hooks/useScreenContext';
import { streamAgentTurnWithReattach } from './agent-stream';
import { clearAgentError, reportAgentError } from './agent-error-store';
import { applyProgress, resetProgress, type AgentProgress } from './progress-store';
import { publishSuggestions } from './suggestion-store';
import { agentMemoriesKey, agentThreadsKey } from './agent-query-keys';
import { createAgentThreadListAdapter } from './agent-thread-list-adapter';

const AgentTokenContext = createContext(0);
const AgentRunThreadContext = createContext<string | undefined>(undefined);

export function useTurnTokens(): number {
  return useContext(AgentTokenContext);
}

export function useRunningThreadId(): string | undefined {
  return useContext(AgentRunThreadContext);
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isUuid(value: string | undefined): value is string {
  return !!value && UUID_PATTERN.test(value);
}

export function AgentRuntimeProvider({ children }: PropsWithChildren) {
  const { agent } = useApiServices();
  const queryClient = useQueryClient();
  const { organization } = useAuth();
  const { threadId, setThreadId } = useAgentThread();
  const screen = useScreenContext();
  const [tokens, setTokens] = useState(0);
  const [runningThreadId, setRunningThreadId] = useState<string | undefined>(undefined);

  const screenRef = useRef(screen);
  screenRef.current = screen;
  const orgRef = useRef(organization?.id);
  orgRef.current = organization?.id;

  /* The list is cached for 15s, so a new thread or a finished turn has to say so. */
  const invalidateThreads = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: agentThreadsKey(orgRef.current) });
  }, [queryClient]);

  const stream = useCallback(
    async function* (
      messages: LangChainMessage[],
      config: {
        abortSignal: AbortSignal;
        command?: { resume: string };
        initialize: () => Promise<{ remoteId: string; externalId: string | undefined }>;
      },
    ) {
      resetProgress();
      clearAgentError();
      setTokens(0);
      setRunningThreadId(undefined);

      const latest = [...messages].reverse().find((message) => message.type === 'human');
      const text = typeof latest?.content === 'string' ? latest.content : undefined;

      try {
        const initialized = await config.initialize();
        const resolvedThreadId = initialized.externalId ?? initialized.remoteId;

        if (!isUuid(resolvedThreadId)) {
          throw new Error(
            `The assistant could not open a conversation (unexpected id "${resolvedThreadId}").`,
          );
        }

        setRunningThreadId(resolvedThreadId);
        invalidateThreads();

        yield* streamAgentTurnWithReattach(
          { openStream: agent.openStream },
          {
            threadId: resolvedThreadId,
            message: config.command ? undefined : text,
            resume: config.command?.resume,
            screen: screenRef.current ?? undefined,
            abortSignal: config.abortSignal,
          },
        );

        void queryClient.invalidateQueries({ queryKey: agentMemoriesKey(orgRef.current) });
        invalidateThreads();
      } catch (error) {
        if (!config.abortSignal.aborted) reportAgentError(error);
        throw error;
      }
    },
    [agent, queryClient, invalidateThreads],
  );

  const threadListAdapter = useMemo(
    () => createAgentThreadListAdapter({ agent, getScreen: () => screenRef.current ?? undefined }),
    [agent],
  );

  const runtime = useLangGraphRuntime({
    unstable_threadListAdapter: threadListAdapter,
    threadId,
    onThreadIdChange: (next) => {
      if (isUuid(next)) setThreadId(next);
    },

    unstable_allowCancellation: true,
    stream,
    load: async (id, options) => {
      const { messages, suggestionStates, pendingQuestion } = await agent.getThread(
        id,
        options?.signal,
      );

      const transcript = pendingQuestion
        ? messages.filter((message) => message.parts?.pendingQuestion?.id !== pendingQuestion.id)
        : messages;

      const lastAssistant = [...transcript].reverse().find((message) => message.role === 'ai');
      publishSuggestions(lastAssistant?.parts?.suggestions ?? [], {
        origin: 'restored',
        settled: suggestionStates.map((state) => state.fingerprint),
      });

      return {
        messages: transcript.map(
          (message): LangChainMessage =>
            message.role === 'human'
              ? { id: message.id, type: 'human', content: message.content }
              : { id: message.id, type: 'ai', content: message.content },
        ),
        interrupts: pendingQuestion ? [{ value: pendingQuestion }] : undefined,
      };
    },
    eventHandlers: {
      onValues: (values) => {
        const frame = values as
          | { suggestions?: AgentSuggestion[]; trace?: { tokensIn?: number; tokensOut?: number }[] }
          | undefined;

        const next = frame?.suggestions;
        if (Array.isArray(next)) publishSuggestions(next, { origin: 'live' });

        if (Array.isArray(frame?.trace)) {
          setTokens(
            frame.trace.reduce(
              (sum, event) => sum + (event.tokensIn ?? 0) + (event.tokensOut ?? 0),
              0,
            ),
          );
        }
      },

      onCustomEvent: (_type, data) => {
        if (isProgress(data)) applyProgress(data);
      },
    },
  });

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <AgentRunThreadContext.Provider value={runningThreadId}>
        <AgentTokenContext.Provider value={tokens}>{children}</AgentTokenContext.Provider>
      </AgentRunThreadContext.Provider>
    </AssistantRuntimeProvider>
  );
}

/** Narrow the untyped custom payload before it reaches the store. */
function isProgress(data: unknown): data is AgentProgress {
  return (
    typeof data === 'object' &&
    data !== null &&
    typeof (data as { kind?: unknown }).kind === 'string'
  );
}
